import { P as Popup, p as popupDefinition } from './definition67.js';
import { M as MenuItem, a as MenuItemRole, m as menuItemDefinition, b as menuName } from './definition30.js';
import { D as DOM, V as VividElement, o as observable, a as attr, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { A as Anchored, a as anchorSlotTemplateFactory } from './anchored.js';
import { D as DelegatesAria, d as delegateAria } from './delegates-aria.js';
import { D as Divider } from './divider.js';
import { g as keyHome, d as keyEnd, e as keyArrowUp, f as keyArrowDown } from './key-codes.js';
import { h as handleEscapeKeyAndStopPropogation } from './index.js';
import { r as ref } from './ref.js';
import { c as classNames } from './class-names.js';
import { s as slotted } from './slotted.js';

const styles = ":host{display:var(--_popup-display, inline)}:host([slotted-anchor]){--_popup-display: contents}.base{display:flex;overflow:hidden auto;box-sizing:border-box;flex-direction:column;padding:4px;gap:8px;inline-size:max-content;max-block-size:var(--menu-block-size, 408px);min-inline-size:var(--menu-min-inline-size)}@media not all and (width >= 600px){.base{max-inline-size:var(--menu-max-inline-size, 300px)}}@media (width >= 600px){.base{max-inline-size:var(--menu-max-inline-size)}}::slotted(a){color:var(--vvd-color-canvas-text);text-decoration:none}::slotted(a[data-vvd-component=menuitem]:focus-visible){box-shadow:0 0 0 4px color-mix(in srgb,var(--vvd-color-cta-500),transparent 85%),inset 0 0 0 3px var(--focus-stroke-gap-color, currentColor);outline:1px solid var(--focus-stroke-color, var(--vvd-color-cta-500));outline-offset:calc(-1px - var(--focus-inset, 0px));--focus-stroke-gap-color: transparent;display:block}.header{padding-block-start:4px;padding-inline:8px}.hide-header .header{display:none}.body{display:flex;flex-direction:column;row-gap:1px}.hide-body .body{display:none}.action-items{display:flex;justify-content:flex-end;gap:8px;padding-block-end:4px;padding-inline:12px}.hide-actions .action-items{display:none}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const isCheckbox = (el) => el instanceof MenuItem && el.controlType === "checkbox";
const isRadio = (el) => el instanceof MenuItem && el.controlType === "radio";
const isSeparator = (el) => el instanceof Divider ? true : el.role === "separator";
const isMenuItemElement = (el) => el instanceof MenuItem ? true : Boolean(el.role && el.role in MenuItemRole);
class Menu extends Anchored(DelegatesAria(VividElement)) {
  constructor() {
    super(...arguments);
    this.expandedItem = null;
    /**
     * The index of the focusable element in the items array
     * defaults to -1
     */
    this.focusIndex = -1;
    /**
     * if focus is moving out of the menu, reset to a stable initial state
     * @internal
     */
    this.handleFocusOut = (e) => {
      if (!this.contains(e.relatedTarget) && this.menuItems !== void 0 && this.menuItems.length) {
        this.collapseExpandedItem();
        this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
        this.menuItems[0].setAttribute("tabindex", "0");
        this.focusIndex = 0;
      }
    };
    this.handleItemFocus = (e) => {
      const targetItem = e.target;
      if (this.menuItems !== void 0 && targetItem !== this.menuItems[this.focusIndex]) {
        this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
        this.focusIndex = this.menuItems.indexOf(targetItem);
        targetItem.setAttribute("tabindex", "0");
      }
    };
    this.handleExpandedChanged = (e) => {
      const changedItem = e.target;
      if (this.expandedItem !== null && changedItem === this.expandedItem && changedItem.expanded === false) {
        this.expandedItem = null;
      }
      if (changedItem.expanded) {
        this.expandedItem = changedItem;
      }
    };
    this.removeItemListeners = () => {
      if (this.menuItems !== void 0) {
        this.menuItems.forEach((item) => {
          item.removeEventListener("expanded-change", this.handleExpandedChanged);
          item.removeEventListener(
            "focus",
            this.handleItemFocus
          );
        });
      }
    };
    this.setItems = () => {
      const newItems = this.domChildren();
      this.removeItemListeners();
      this.menuItems = newItems.filter(isMenuItemElement);
      if (this.menuItems.length) {
        this.focusIndex = 0;
      }
      this.menuItems.forEach((item, index) => {
        if (item instanceof MenuItem) {
          item._isPresentational = false;
        }
        item.setAttribute("tabindex", index === 0 ? "0" : "-1");
        item.addEventListener("expanded-change", this.handleExpandedChanged);
        item.addEventListener("focus", this.handleItemFocus);
      });
    };
    this.placement = "bottom";
    this.autoDismiss = false;
    this.positionStrategy = "fixed";
    this.open = false;
    this.#onAnchorClick = () => {
      if (this.#triggerBehaviour === "off") {
        return;
      }
      if (this.#triggerBehaviour === "legacy" && this.open) {
        return;
      }
      const newValue = !this.open;
      DOM.queueUpdate(() => this.open = newValue);
    };
    this._onFocusout = (e) => {
      const focusTarget = e.relatedTarget;
      const focusMovedAway = !this.contains(focusTarget) && !this._anchorEl?.contains(focusTarget);
      if (this.autoDismiss && focusMovedAway) {
        this.open = false;
      }
    };
  }
  /**
   * @internal
   */
  itemsChanged() {
    if (this.$fastController.isConnected && this.menuItems !== void 0) {
      this.setItems();
    }
  }
  /**
   * @internal
   */
  connectedCallback() {
    super.connectedCallback();
    DOM.queueUpdate(() => {
      this.setItems();
    });
  }
  /**
   * @internal
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeItemListeners();
    this.menuItems = void 0;
  }
  /**
   * Moves focus into the menu. If there is a child with the `autofocus` attribute, it will be focused.
   * Otherwise, the first focusable child will be focused.
   *
   * @public
   */
  focus() {
    const autoFocusElement = this.querySelector(
      '[autofocus]:not([slot="anchor"])'
    );
    if (autoFocusElement instanceof HTMLElement) {
      autoFocusElement.focus();
    } else {
      this.setFocus(0);
    }
  }
  /**
   * Collapses any expanded Menu Items.
   *
   * @public
   */
  collapseExpandedItem() {
    if (this.expandedItem !== null) {
      this.expandedItem.expanded = false;
      this.expandedItem = null;
    }
  }
  /**
   * @internal
   */
  handleMenuKeyDown(e) {
    if (e.defaultPrevented || this.menuItems === void 0) {
      return;
    }
    switch (e.key) {
      case keyArrowDown:
        this.setFocus(this.focusIndex + 1);
        return;
      case keyArrowUp:
        this.setFocus(this.focusIndex - 1);
        return;
      case keyEnd:
        this.setFocus(this.menuItems.length - 1);
        return;
      case keyHome:
        this.setFocus(0);
        return;
      default:
        return true;
    }
  }
  /**
   * get an array of valid DOM children
   */
  domChildren() {
    return Array.from(this.children).filter((child) => !child.hasAttribute("hidden")).filter((child) => !child.hasAttribute("slot"));
  }
  setFocus(focusIndex) {
    if (this.menuItems === void 0) {
      return;
    }
    if (focusIndex >= 0 && focusIndex < this.menuItems.length) {
      const child = this.menuItems[focusIndex];
      if (this.focusIndex > -1 && this.menuItems.length >= this.focusIndex - 1) {
        this.menuItems[this.focusIndex].setAttribute("tabindex", "-1");
      }
      this.focusIndex = focusIndex;
      child.setAttribute("tabindex", "0");
      child.focus();
    }
  }
  get #triggerBehaviour() {
    return this.trigger ?? "legacy";
  }
  openChanged(_, newValue) {
    if (newValue) {
      this._popupEl?.show().then(() => this.focus());
    }
    newValue ? this.$emit("open", void 0, { bubbles: false }) : this.$emit("close", void 0, { bubbles: false });
    if (this._anchorEl) {
      this.#updateAnchor(this._anchorEl);
    }
  }
  /**
   * @internal
   */
  _anchorElChanged(oldValue, newValue) {
    if (oldValue) this.#cleanupAnchor(oldValue);
    if (newValue) this.#setupAnchor(newValue);
  }
  #setupAnchor(a) {
    a.addEventListener("click", this.#onAnchorClick, true);
    a.addEventListener("focusout", this._onFocusout);
    a.setAttribute("aria-haspopup", "menu");
    this.#updateAnchor(a);
  }
  #updateAnchor(a) {
    a.setAttribute("aria-expanded", this.open.toString());
    a.setAttribute("data-expanded", this.open.toString());
  }
  #cleanupAnchor(a) {
    a.removeEventListener("click", this.#onAnchorClick, true);
    a.removeEventListener("focusout", this._onFocusout);
    a.removeAttribute("aria-haspopup");
    a.removeAttribute("aria-expanded");
    a.removeAttribute("data-expanded");
  }
  #onAnchorClick;
  /**
   * @internal
   */
  _onChange(e) {
    if (this.menuItems === void 0 || !(e.target instanceof Element)) {
      return;
    }
    if (this.#triggerBehaviour === "auto" && !isCheckbox(e.target)) {
      this.open = false;
    }
    const domChildren = this.domChildren();
    const targetItemIndex = domChildren.indexOf(e.target);
    if (targetItemIndex === -1) {
      return;
    }
    if (isRadio(e.target) && e.target.checked) {
      for (let i = targetItemIndex - 1; i >= 0; --i) {
        const item = domChildren[i];
        if (isRadio(item)) {
          item.checked = false;
        }
        if (isSeparator(item)) {
          break;
        }
      }
      for (let i = targetItemIndex + 1; i <= domChildren.length - 1; ++i) {
        const item = domChildren[i];
        if (isRadio(item)) {
          item.checked = false;
        }
        if (isSeparator(item)) {
          break;
        }
      }
    }
    return true;
  }
}
__decorateClass([
  observable
], Menu.prototype, "items");
__decorateClass([
  attr({ mode: "fromView" })
], Menu.prototype, "placement");
__decorateClass([
  attr
], Menu.prototype, "trigger");
__decorateClass([
  attr({ mode: "boolean", attribute: "auto-dismiss" })
], Menu.prototype, "autoDismiss");
__decorateClass([
  attr({ mode: "fromView", attribute: "position-strategy" })
], Menu.prototype, "positionStrategy");
__decorateClass([
  attr({ mode: "boolean" })
], Menu.prototype, "open");
__decorateClass([
  observable
], Menu.prototype, "headerSlottedContent");
__decorateClass([
  observable
], Menu.prototype, "actionItemsSlottedContent");
__decorateClass([
  observable
], Menu.prototype, "_popupOffset");

const getClasses = ({
  headerSlottedContent,
  actionItemsSlottedContent,
  items
}) => classNames(
  "base",
  ["hide-header", !headerSlottedContent?.length],
  ["hide-actions", !actionItemsSlottedContent?.length],
  ["hide-body", items && !items.length]
);
function handleEscapeKey(menu, event) {
  if (menu.open && handleEscapeKeyAndStopPropogation(event)) {
    menu.open = false;
  }
  return true;
}
const MenuTemplate = (context) => {
  const popupTag = context.tagFor(Popup);
  const anchorSlotTemplate = anchorSlotTemplateFactory();
  function handlePopupEvents(x, e, state) {
    e.stopPropagation();
    x.open = state;
  }
  return html`
		<template @change="${(x, c) => x._onChange(c.event)}"
							@focusout="${(x, c) => x._onFocusout(c.event)}">
			${anchorSlotTemplate}
			<${popupTag}
				${ref("_popupEl")}
				:placement=${(x) => x.placement}
				:open=${(x) => x.open}
				:anchor=${(x) => x._anchorEl}
				:strategy="${(x) => x.positionStrategy}"
				:offset=${(x) => x._popupOffset}
				@keydown="${(x, c) => handleEscapeKey(x, c.event)}"
				@vwc-popup:open="${(x, c) => handlePopupEvents(x, c.event, true)}"
				@vwc-popup:close="${(x, c) => handlePopupEvents(x, c.event, false)}"
			>
			<div class="${getClasses}">
				<div class="header">
					<slot name="header" ${slotted("headerSlottedContent")}></slot>
				</div>
				<div
					class="body"
					${delegateAria({
    role: "menu"
  })}
					@keydown="${(x, c) => x.handleMenuKeyDown(c.event)}"
					@focusout="${(x, c) => x.handleFocusOut(c.event)}"
				>
					<slot ${slotted("items")}></slot>
				</div>
				<footer class="action-items"><slot name="action-items"  ${slotted(
    "actionItemsSlottedContent"
  )}></slot></footer>
			</div>
		</${popupTag}>
	</template>`;
};

const menuDefinition = defineVividComponent(
  menuName,
  Menu,
  MenuTemplate,
  [popupDefinition, menuItemDefinition],
  {
    styles
  }
);
const registerMenu = createRegisterFunction(menuDefinition);

export { Menu as M, menuDefinition as m, registerMenu as r };
